import React, { useEffect, useState } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonContent,
  IonList,
  IonItem,
  IonLabel,
  IonButton,
  IonIcon,
  IonInput,
  IonModal,
  IonFooter,
  IonSpinner,
  IonText,
  IonToast,
} from '@ionic/react';
import { addOutline, personAddOutline, shareOutline } from 'ionicons/icons';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { apiUrl, apiHeaders } from '../lib/api';
import { logActivity } from '../lib/logger';
import { useI18n } from '../i18n/I18nProvider';
import ShareLinkSheet from '../components/ShareLinkSheet';
import {
  buildDeepLinkShareText,
  buildTopicDeepLink,
} from '../lib/deepLinks';

interface Conversation {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

interface TopicMeta {
  id: string;
  name: string;
  description: string | null;
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
  role: string | null;
}

interface RouteParams {
  topicId: string;
}

type Props = RouteComponentProps<RouteParams>;

const ConversationsPage: React.FC<Props> = ({ match }) => {
  const history = useHistory();
  const { getToken } = useAuth();
  const topicId = match.params.topicId;
  const { t } = useI18n();

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [topic, setTopic] = useState<TopicMeta | null>(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [showShareSheet, setShowShareSheet] = useState(false);
  const [shareToast, setShareToast] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function loadConversationsAndTopic() {
      try {
        setLoading(true);
        setError(null);

        const token = await getToken();
        const [topicRes, convRes] = await Promise.all([
          fetch(apiUrl(`/api/chat/topics/${topicId}`), {
            headers: {
              'Content-Type': 'application/json',
              ...apiHeaders(token),
            },
          }),
          fetch(
          apiUrl(`/api/chat/topics/${topicId}/conversations`),
          {
            headers: {
              'Content-Type': 'application/json',
              ...apiHeaders(token),
            },
            }
          ),
        ]);

        if (!topicRes.ok) {
          throw new Error(`Failed to load topic (${topicRes.status})`);
        }

        if (!convRes.ok) {
          throw new Error(`Failed to load conversations (${convRes.status})`);
        }

        const topicJson = (await topicRes.json()) as TopicMeta;
        const convJson = (await convRes.json()) as Conversation[];
        if (isMounted) {
          setTopic(topicJson);
          setConversations(convJson);
        }
      } catch (err) {
        if (isMounted) {
          setError(
            err instanceof Error
              ? err.message
              : t('conversations.error.loadFailed')
          );
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadConversationsAndTopic();

    return () => {
      isMounted = false;
    };
  }, [getToken, topicId, t]);

  const handleOpenConversation = async (conversation: Conversation) => {
    // Best-effort: log that this conversation was opened
    try {
      await logActivity('chat_open', {
        conversationId: conversation.id,
        conversationName: conversation.name,
      });
    } catch {
      // ignore logging failures
    }

    history.push(`/chat/${conversation.id}`);
  };

  const handleCreateConversation = async () => {
    if (!newName.trim()) {
      setError(t('conversations.error.nameRequired'));
      return;
    }

    try {
      setCreating(true);
      setError(null);

      const token = await getToken();
      const res = await fetch(
        apiUrl(`/api/chat/topics/${topicId}/conversations`),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...apiHeaders(token),
          },
          body: JSON.stringify({
            name: newName.trim(),
          }),
        }
      );

      if (!res.ok) {
        const body = await res.json().catch(() => undefined);
        const msg =
          body?.message ||
          `${t('conversations.error.createFailed')} (${res.status})`;
        throw new Error(msg);
      }

      const created = (await res.json()) as Conversation;
      setConversations((prev) => [...prev, created]);
      setShowCreateModal(false);
      setNewName('');
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('conversations.error.createFailed')
      );
    } finally {
      setCreating(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) {
      setError(t('conversations.invite.emailRequired'));
      return;
    }

    try {
      setInviting(true);
      setError(null);

      const token = await getToken();
      const res = await fetch(
        apiUrl(`/api/chat/topics/${topicId}/invite`),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...apiHeaders(token),
          },
          body: JSON.stringify({
            email: inviteEmail.trim(),
          }),
        }
      );

      if (!res.ok) {
        const body = await res.json().catch(() => undefined);
        const msg =
          body?.message ||
          `${t('conversations.invite.sendFailed')} (${res.status})`;
        throw new Error(msg);
      }

      setShowInviteModal(false);
      setInviteEmail('');
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('conversations.invite.sendFailed')
      );
    } finally {
      setInviting(false);
    }
  };

  const canInvite =
    topic && (topic.role === 'OWNER' || topic.role === 'ADMIN');
  const canCreateConversation =
    !!topic?.role &&
    (topic.role === 'MEMBER' ||
      topic.role === 'MODERATOR' ||
      topic.role === 'ADMIN' ||
      topic.role === 'OWNER');

  return (
    <IonPage>
      <IonHeader translucent>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/topics" text={t('common.actions.back')} />
          </IonButtons>
          <IonTitle>{t('conversations.title')}</IonTitle>
          <IonButtons slot="end">
            <IonButton
              onClick={() => setShowShareSheet(true)}
              aria-label={t('deepLink.topic.shareAria')}
            >
              <IonIcon slot="icon-only" icon={shareOutline} />
            </IonButton>
            {canInvite && (
              <IonButton
                onClick={() => setShowInviteModal(true)}
                aria-label={t('conversations.invite.title')}
              >
                <IonIcon slot="start" icon={personAddOutline} />
                {t('conversations.invite.title')}
              </IonButton>
            )}
            {canCreateConversation && (
              <IonButton onClick={() => setShowCreateModal(true)}>
                <IonIcon slot="icon-only" icon={addOutline} />
              </IonButton>
            )}
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className="ion-padding vault-screen">
        {loading && (
          <div className="ion-text-center ion-padding">
            <IonSpinner name="crescent" />
          </div>
        )}

        {error && !loading && (
          <IonText color="danger">
            <p>{error}</p>
          </IonText>
        )}

        {!loading && !error && conversations.length === 0 && (
          <IonText color="medium">
            <p>{t('conversations.empty')}</p>
          </IonText>
        )}

        <IonList className="vault-feed-list">
          {conversations.map((c) => (
            <IonItem
              key={c.id}
              button
              detail
              lines="none"
              className="vault-feed-item"
              onClick={() => handleOpenConversation(c)}
            >
              <IonLabel>
                <h2>{c.name}</h2>
              </IonLabel>
            </IonItem>
          ))}
        </IonList>
      </IonContent>

      <IonModal
        className="vault-glass-modal"
        isOpen={showCreateModal}
        onDidDismiss={() => setShowCreateModal(false)}
      >
        <IonHeader>
          <IonToolbar>
            <IonTitle>{t('conversations.modal.newConversation.title')}</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <IonItem lines="none" className="vault-field-item">
            <IonLabel position="stacked">
              {t('conversations.modal.name.label')}
            </IonLabel>
            <IonInput
              value={newName}
              onIonChange={(e) => setNewName(e.detail.value ?? '')}
              placeholder={t('conversations.modal.name.placeholder')}
            />
          </IonItem>
          {error && (
            <IonText color="danger">
              <p>{error}</p>
            </IonText>
          )}
        </IonContent>
        <IonFooter>
          <IonToolbar>
            <IonButtons slot="start">
              <IonButton onClick={() => setShowCreateModal(false)}>
                {t('conversations.modal.actions.cancel')}
              </IonButton>
            </IonButtons>
            <IonButtons slot="end">
              <IonButton onClick={handleCreateConversation} disabled={creating}>
                {creating ? (
                  <IonSpinner name="crescent" />
                ) : (
                  t('conversations.modal.actions.create')
                )}
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonFooter>
      </IonModal>

      <IonModal
        className="vault-glass-modal"
        isOpen={showInviteModal}
        onDidDismiss={() => setShowInviteModal(false)}
      >
        <IonHeader>
          <IonToolbar>
            <IonTitle>{t('conversations.invite.title')}</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <IonItem lines="none" className="vault-field-item">
            <IonLabel position="stacked">{t('conversations.invite.emailLabel')}</IonLabel>
            <IonInput
              type="email"
              value={inviteEmail}
              onIonChange={(e) => setInviteEmail(e.detail.value ?? '')}
              placeholder={t('conversations.invite.emailPlaceholder')}
            />
          </IonItem>
          {error && (
            <IonText color="danger">
              <p>{error}</p>
            </IonText>
          )}
        </IonContent>
        <IonFooter>
          <IonToolbar>
            <IonButtons slot="start">
              <IonButton onClick={() => setShowInviteModal(false)}>
                {t('common.actions.cancel')}
              </IonButton>
            </IonButtons>
            <IonButtons slot="end">
              <IonButton onClick={handleInvite} disabled={inviting}>
                {inviting ? (
                  <IonSpinner name="crescent" />
                ) : (
                  t('conversations.invite.send')
                )}
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonFooter>
      </IonModal>

      <ShareLinkSheet
        isOpen={showShareSheet}
        url={buildTopicDeepLink(topicId)}
        shareText={buildDeepLinkShareText(
          'topic',
          buildTopicDeepLink(topicId),
          topic?.name
        )}
        header={t('deepLink.topic.shareTitle')}
        subHeader={t('deepLink.topic.shareBody')}
        onDidDismiss={() => setShowShareSheet(false)}
        onCopied={() => setShareToast(t('shareLink.copied'))}
      />
      <IonToast
        isOpen={!!shareToast}
        message={shareToast ?? ''}
        duration={1600}
        onDidDismiss={() => setShareToast(null)}
      />
    </IonPage>
  );
};

export default ConversationsPage;

